import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, of, switchMap, map } from 'rxjs';
import {
  FUNDS_AGREEMENT_PATH,
  FUNDS_FIRM_PATH,
  PUBLIC_ACCESS_PATH,
  PUBLIC_ERROR_PATH,
  PUBLIC_LOGIN_PATH,
  LOGOUT_REASONS,
  SECURITY_AUTHENTICATE_PATH,
  SECURITY_VALIDATE_SESSION_PATH,
  LOGOUT,
} from 'src/app/constants/NextGenConstants';
import { v4 as uuidv4 } from 'uuid';

export enum AuthenticationType {
  SUCCESS = 'SUCCESS',
  ACCESS_DENIED = 'ACCESS_DENIED',
  ENTITLEMENT_MISSING = 'ENTITLEMENT_MISSING',
  FAIL = 'FAIL',
  TERMS_NOT_ACCEPTED = 'TERMS_NOT_ACCEPTED',
}

interface AuthenticationApiResponse {
  authenticationType: AuthenticationType;
}

/**
 * LoginService handles authentication state and user id management.
 * It provides methods for login, logout, and user info retrieval.
 */

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  /** Indicates if the user is currently authenticated. */
  private isLoggedIn: boolean = false;
  private isAuthorized: boolean = false;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  /**
   * Returns true if the user is authenticated, false otherwise.
   */
  isUserAuthenticated(): boolean {
    return this.isLoggedIn;
  }

  /**
   * Returns true if the user is authorized, false otherwise.
   */
  isUserAuthorized(): boolean {
    return this.isAuthorized;
  }

  /**
   * Logs out the user and resets authentication state.
   */
  logout(): void {
    this.isLoggedIn = false;
    this.isAuthorized = false;
  }
  /**
   * Attempts to log in the user by calling the /authenticate endpoint.
   *
   * HTTP status code is the source of truth:
   * - 200 OK = authentication succeeded
   * - 401 Unauthorized = session expired or invalid at the upstream AM layer; navigate to session-timeout page
   * - 403 Forbidden = access denied; reads authenticationType from response body to determine routing
   * - 409 Conflict = prerequisite not yet satisfied; reads authenticationType from response body to determine routing:
   *     - ENTITLEMENT_MISSING -> navigate to entitlement selection page
   *     - TERMS_NOT_ACCEPTED -> navigate to terms acceptance page
   *     - ACCESS_DENIED → navigate to access denied page
   * - 500 or missing/unknown reason -> default to error page (secure fail)
   *
   * @returns Observable<boolean> indicating login success.
   */
  /* eslint-disable */
  // prettier-ignore
  login(entitlementGroup?: string): Observable<boolean> {
    this.logout();
    const params = entitlementGroup? new HttpParams().set('entitlementGroup', entitlementGroup) : undefined;
    return this.http
      .post<AuthenticationApiResponse>(SECURITY_AUTHENTICATE_PATH, null, { headers: this.contentTypeHeader, params, observe: 'response' })
      .pipe(
        switchMap((response) => {
          // 200 OK received; authentication succeeded
          this.isLoggedIn = true;
          this.isAuthorized = true;
          return of(true);
        }),
        catchError((error) => {
          // HTTP status code indicated failure.
          this.isLoggedIn = false;
          this.isAuthorized = false;
          
          if (error instanceof HttpErrorResponse) {
            if (error.status === 401) {
              // Session expired or invalid at the AM layer.
              this.router.navigate([PUBLIC_LOGIN_PATH], {
                queryParams: { ccview: LOGOUT, reason: LOGOUT_REASONS[1] },
              });
            } else if (error.status === 403 || error.status === 409) {
              // Authorization/prerequisite check failed — use typed response body reason.
              const authReason = error.error?.authenticationType ?? '';
              if (authReason === 'ENTITLEMENT_MISSING') {
                this.isLoggedIn = true;
                this.router.navigate([FUNDS_FIRM_PATH]);
              } else if (authReason === 'TERMS_NOT_ACCEPTED') {
                this.isLoggedIn = true;
                this.router.navigate([FUNDS_AGREEMENT_PATH]);
              } else if (authReason === 'ACCESS_DENIED') {
                this.router.navigate([PUBLIC_ACCESS_PATH]);
              } else {
                // Missing/unknown reason — default to error page (secure fail)
                this.router.navigate([PUBLIC_ERROR_PATH]);
              }
            } else {
              // Other errors
              this.router.navigate([PUBLIC_ERROR_PATH]);
            }
          }
          return of(false);
        }),
      );
  }
  /* eslint-enable */

  /* eslint-disable */
  // prettier-ignore
  validateSession() : Observable<boolean>{
    return this.http
    .get<{valid: boolean}>(SECURITY_VALIDATE_SESSION_PATH, { headers: this.contentTypeHeader })
    .pipe(
      map((response)=>{
        if(response.valid){
          this.isLoggedIn = true;
          this.isAuthorized = true;
        }
        return response.valid;
      }),
      catchError((error) => {
        this.isLoggedIn = false;
        this.isAuthorized = false;
        return of(false);
      }),
    );
      
  }

  /* eslint-enable */

  /**
   * Returns HTTP headers with content type and a unique tracking ID.
   */
  private get contentTypeHeader(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'FID-LOG-TRACKING-ID': uuidv4(),
    });
  }
}
